import { platformWordmarkUrl, useBrandStore } from '@/brand'
import { cn } from '@/design'
import { BrandMark } from './brand-mark'

const HEIGHT = { sm: 'h-7', md: 'h-9', lg: 'h-12' } as const
const TEXT = { sm: 'text-[16px]', md: 'text-[17px]', lg: 'text-[24px]' } as const

/**
 * The brand as a wordmark. For the platform brand this is the client's logo image (which already
 * carries the name, so no text is drawn beside it); for a tenant brand it is the square mark plus
 * the product name, exactly as before. Height is fixed and width follows the image's own aspect
 * ratio, so the logo is never stretched or cropped at any viewport. `onDark` adds a white tile.
 */
export function BrandLogo({
  size = 'md',
  onDark = false,
  className,
}: {
  readonly size?: keyof typeof HEIGHT
  readonly onDark?: boolean
  readonly className?: string
}) {
  const brand = useBrandStore((state) => state.brand)
  const wordmark = platformWordmarkUrl(brand)
  if (wordmark === null) {
    return (
      <span className={cn('inline-flex min-w-0 items-center gap-2.5', className)}>
        <BrandMark size={size} onDark={onDark} />
        <span
          className={cn(
            'truncate font-display font-semibold',
            TEXT[size],
            onDark ? 'text-white' : 'text-ink',
          )}
        >
          {brand.product_name}
        </span>
      </span>
    )
  }
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center',
        onDark && 'rounded-md bg-white px-2 py-1',
        className,
      )}
    >
      <img
        src={wordmark}
        alt={brand.product_name}
        className={cn('w-auto max-w-full object-contain', HEIGHT[size])}
      />
    </span>
  )
}
